
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { createMessageNotification } from '@/utils/notification-utils';

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  created_at: string;
  sender_name?: string;
  sender_avatar?: string;
}

interface Connection {
  id: string;
  full_name: string;
  avatar_url?: string;
  last_message?: string;
  last_message_time?: string;
}

export const useMessages = () => {
  const { userId } = useParams<{ userId?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedUser, setSelectedUser] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch mutual connections
  useEffect(() => {
    const fetchConnections = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        const { data: mutualFollows, error } = await supabase
          .from('user_follows')
          .select(`
            following_id,
            profiles!following_id(
              id,
              full_name,
              avatar_url
            )
          `)
          .eq('follower_id', user.id);
          
        if (error) throw error;
        
        const mutualConnections: Connection[] = [];
        
        for (const follow of mutualFollows || []) {
          if (follow.profiles && typeof follow.profiles === 'object') {
            const profile = follow.profiles as any;
            
            const { data: followsBack } = await supabase
              .from('user_follows')
              .select('*')
              .eq('follower_id', follow.following_id)
              .eq('following_id', user.id)
              .single();
              
            if (followsBack) {
              mutualConnections.push({
                id: profile.id,
                full_name: profile.full_name || 'Unknown User',
                avatar_url: profile.avatar_url
              });
            }
          }
        }
        
        setConnections(mutualConnections);
        
        if (userId) {
          const selected = mutualConnections.find(c => c.id === userId);
          if (selected) {
            setSelectedUser(selected);
          } else {
            const { data: userProfile } = await supabase
              .from('profiles')
              .select('id, full_name, avatar_url')
              .eq('id', userId)
              .single();
              
            if (userProfile) {
              setSelectedUser({
                id: userProfile.id,
                full_name: userProfile.full_name || 'Unknown User',
                avatar_url: userProfile.avatar_url
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching connections:', error);
        setConnections([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConnections();
  }, [user, userId]);

  // Fetch messages for selected user
  useEffect(() => {
    const fetchMessages = async () => {
      if (!user || !selectedUser) return;
      
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('messages')
          .select(`
            *,
            sender:profiles!sender_id(
              full_name,
              avatar_url
            )
          `)
          .or(`and(sender_id.eq.${user.id},recipient_id.eq.${selectedUser.id}),and(sender_id.eq.${selectedUser.id},recipient_id.eq.${user.id})`)
          .order('created_at', { ascending: true });
          
        if (error) throw error;
        
        const formattedMessages = (data || []).map(msg => ({
          id: msg.id,
          sender_id: msg.sender_id,
          recipient_id: msg.recipient_id,
          content: msg.content,
          created_at: msg.created_at,
          sender_name: (msg.sender as any)?.full_name,
          sender_avatar: (msg.sender as any)?.avatar_url
        }));
        
        setMessages(formattedMessages);
      } catch (error) {
        console.error('Error fetching messages:', error);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
    
    if (user && selectedUser) {
      const channel = supabase
        .channel('messages')
        .on('postgres_changes', { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'messages',
          filter: `or(and(sender_id=eq.${user.id},recipient_id=eq.${selectedUser.id}),and(sender_id=eq.${selectedUser.id},recipient_id=eq.${user.id}))` 
        }, async (payload) => {
          const newMessage = payload.new as Message;
          
          const { data: sender } = await supabase
            .from('profiles')
            .select('full_name, avatar_url')
            .eq('id', newMessage.sender_id)
            .single();
            
          setMessages(prev => [...prev, {
            ...newMessage,
            sender_name: sender?.full_name,
            sender_avatar: sender?.avatar_url
          }]);
        })
        .subscribe();
        
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, selectedUser]);

  const handleSelectUser = (connection: Connection) => {
    setSelectedUser(connection);
    navigate(`/messages/${connection.id}`);
  };

  const handleSendMessage = async (messageContent: string) => {
    if (!user || !selectedUser) return;
    
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: selectedUser.id,
          content: messageContent
        });
        
      if (error) throw error;

      const { data: currentUserProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      if (currentUserProfile?.full_name) {
        await createMessageNotification(selectedUser.id, currentUserProfile.full_name);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return {
    connections,
    selectedUser,
    messages,
    loading,
    handleSelectUser,
    handleSendMessage
  };
};
